import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CreditCard,
  FileText,
  AlertCircle,
  CheckCircle2,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { useUserProfile } from '../../context/UserProfileContext';
import { api } from '../../lib/api';

// ── Stripe setup ───────────────────────────────────────────────────────────
const STRIPE_PK_TEST = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY_TEST as string | undefined;
const STRIPE_PK_LIVE = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY_LIVE as string | undefined;

/** Cache loadStripe promises so we don't re-init for the same key. */
const stripePromiseCache: Record<string, ReturnType<typeof loadStripe>> = {};
function getStripePromise(mode: 'test' | 'live') {
  const key = mode === 'test' ? STRIPE_PK_TEST : STRIPE_PK_LIVE;
  if (!key) return null;
  if (!stripePromiseCache[key]) stripePromiseCache[key] = loadStripe(key);
  return stripePromiseCache[key];
}

// ── Types ──────────────────────────────────────────────────────────────────
interface StripeData {
  customerId?: string;
  subscriptionId?: string;
  status?: string;
  subscriptionQuantity?: number;
}

interface PaymentMethodData {
  id: string;
  type: string;
  card: {
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
  } | null;
}

interface InvoiceData {
  id: string;
  number: string | null;
  status: string | null;
  amountDue: number;
  amountPaid: number;
  currency: string;
  created: number;
  periodStart: number;
  periodEnd: number;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
}

// ── Inner form that uses Stripe hooks (must be inside <Elements>) ──────────
function PaymentMethodForm({ onSuccess }: { onSuccess: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSaving(true);
    setError(null);

    const result = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/admin/company/billing/payments`,
      },
    });

    if (result.error) {
      setError(result.error.message ?? 'Something went wrong.');
      setSaving(false);
    } else {
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      {error && (
        <div className="flex items-start gap-2 text-small text-red-600">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      <button
        type="submit"
        disabled={!stripe || saving}
        className="btn btn-primary w-full"
      >
        {saving ? 'Saving…' : 'Save Payment Method'}
      </button>
    </form>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatCardBrand(brand: string): string {
  const brands: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    discover: 'Discover',
    diners: 'Diners Club',
    jcb: 'JCB',
    unionpay: 'UnionPay',
  };
  return brands[brand] ?? brand.charAt(0).toUpperCase() + brand.slice(1);
}

function formatDate(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount / 100);
}

function invoiceStatusColor(status: string | null): string {
  switch (status) {
    case 'paid': return 'bg-green-100 text-green-700';
    case 'open': return 'bg-blue-100 text-blue-700';
    case 'draft': return 'bg-slate-100 text-slate-600';
    case 'void': return 'bg-stone text-slate';
    case 'uncollectible': return 'bg-red-100 text-red-700';
    default: return 'bg-stone text-slate';
  }
}

// ── Main page component ────────────────────────────────────────────────────
export function ManagePaymentsPage() {
  const navigate = useNavigate();
  const { currentUser, adminOrg, stripeMode } = useUserProfile();

  const [billing, setBilling] = useState<StripeData | null>(null);
  const [loadingBilling, setLoadingBilling] = useState(true);

  // Test card info (only loaded for demo orgs)
  const [testCardNumber, setTestCardNumber] = useState<string | null>(null);
  const isDemoOrg = stripeMode === 'test';

  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodData | null | undefined>(undefined);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [setupClientSecret, setSetupClientSecret] = useState<string | null>(null);

  // Invoices state
  const [invoices, setInvoices] = useState<InvoiceData[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);

  const stripePromise = getStripePromise(stripeMode);
  const stripeConfigured = !!stripePromise;
  const hasSubscription = !!billing?.subscriptionId;

  // Load subscription, payment method, and invoices in parallel
  useEffect(() => {
    let cancelled = false;

    const subPromise = api
      .get<{ success: boolean; data: StripeData | null }>('/billing/subscription')
      .then((res) => { if (!cancelled && res.data) setBilling(res.data); })
      .catch(() => { /* ignore */ });

    const pmPromise = api
      .get<{ success: boolean; data: PaymentMethodData | null }>('/billing/payment-method')
      .then((res) => { if (!cancelled) setPaymentMethod(res.data); })
      .catch(() => { if (!cancelled) setPaymentMethod(null); });

    const invPromise = api
      .get<{ success: boolean; data: InvoiceData[] }>('/billing/invoices')
      .then((res) => { if (!cancelled) setInvoices(res.data); })
      .catch(() => { /* ignore */ })
      .finally(() => { if (!cancelled) setInvoicesLoading(false); });

    Promise.all([subPromise, pmPromise, invPromise]).finally(() => {
      if (!cancelled) setLoadingBilling(false);
    });

    return () => { cancelled = true; };
  }, []);

  // Load test card number for demo orgs
  useEffect(() => {
    if (!isDemoOrg) return;
    let cancelled = false;
    api.get<{ success: boolean; data: { cardNumber: string } | null }>('/billing/test-card')
      .then((res) => { if (!cancelled && res.data) setTestCardNumber(res.data.cardNumber); })
      .catch(() => { /* ignore — test card is informational only */ });
    return () => { cancelled = true; };
  }, [isDemoOrg]);

  // Load SetupIntent client secret for payment method form
  useEffect(() => {
    if (!hasSubscription || !stripeConfigured) return;
    let cancelled = false;
    api.post<{ success: boolean; data: { clientSecret: string } }>('/billing/create-setup-intent')
      .then((res) => {
        if (!cancelled) setSetupClientSecret(res.data.clientSecret);
      })
      .catch(() => { /* payment method form will be hidden */ });
    return () => { cancelled = true; };
  }, [hasSubscription, stripeConfigured]);

  // Guard: Platform Admin or Org Admin
  if (!currentUser?.isPlatformAdmin && !adminOrg) {
    return (
      <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-2xl md:mx-auto">
        <p className="text-small text-slate text-center py-16">
          You don't have access to billing management.
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-2xl md:mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={() => navigate('/admin/company/billing')}
          className="shrink-0 w-10 h-10 flex items-center justify-center rounded-lg
                     hover:bg-mist active:bg-stone transition-colors"
        >
          <ArrowLeft size={20} className="text-midnight" />
        </button>
        <div>
          <h1 className="font-display text-h1 text-midnight">Manage Billing</h1>
          <p className="text-small text-slate mt-0.5">
            Payment method and invoices.
          </p>
        </div>
      </div>

      {/* Test card info banner (demo orgs only) */}
      {isDemoOrg && testCardNumber && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard size={18} className="text-amber-700" />
            <p className="text-small font-semibold text-amber-800">Stripe Test Card</p>
          </div>
          <p className="text-[13px] text-amber-700 mb-2 leading-relaxed">
            This is a test card number provided by Stripe for testing in the Sandbox/Test
            environment. It will not result in a real charge.
          </p>
          <div className="bg-white border border-amber-200 rounded-lg px-3 py-2.5 mb-2">
            <p className="font-mono text-body tracking-wider text-midnight text-center select-all">
              {testCardNumber.replace(/(.{4})/g, '$1 ').trim()}
            </p>
          </div>
          <div className="flex flex-col gap-0.5 text-[12px] text-amber-700">
            <span><span className="font-semibold">Expiration date:</span> Any date in the future</span>
            <span><span className="font-semibold">Security code (CVC):</span> Any three digits</span>
          </div>
        </div>
      )}

      {/* Loading */}
      {loadingBilling && (
        <div className="flex items-center justify-center py-8">
          <Loader2 size={24} className="animate-spin text-plum" />
        </div>
      )}

      {/* No subscription notice */}
      {!loadingBilling && !hasSubscription && (
        <div className="bg-mist border border-stone rounded-xl p-4 mb-4">
          <p className="text-small text-slate leading-relaxed">
            No active subscription. Set up a plan first to manage payment and view invoices.
          </p>
        </div>
      )}

      {/* ── Section: Current Payment Method ──────────────────────────── */}
      {!loadingBilling && hasSubscription && (
        <section className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm mb-4">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard size={18} className="text-plum" />
            <h2 className="font-display text-h3 text-midnight">Payment Method</h2>
          </div>

          {/* Current card on file */}
          {paymentMethod?.card && (
            <div className="bg-mist rounded-lg px-4 py-3 mb-4 flex items-center gap-3">
              <CreditCard size={20} className="text-plum shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-small font-medium text-midnight">
                  {formatCardBrand(paymentMethod.card.brand)} ending in {paymentMethod.card.last4}
                </p>
                <p className="text-[13px] text-slate">
                  Expires {String(paymentMethod.card.expMonth).padStart(2, '0')}/{paymentMethod.card.expYear}
                </p>
              </div>
            </div>
          )}

          {paymentMethod === null && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4 flex items-start gap-2">
              <AlertCircle size={16} className="shrink-0 text-amber-600 mt-0.5" />
              <p className="text-[13px] text-amber-700">
                No payment method on file. Add one below to keep your subscription active.
              </p>
            </div>
          )}

          {/* Stripe payment form */}
          {stripeConfigured && setupClientSecret ? (
            <>
              <p className="text-small text-slate mb-4">
                {paymentMethod?.card
                  ? 'Update your payment method below. This will be used for all future invoices.'
                  : 'Add a payment method. This will be used for all future invoices.'}
              </p>
              {paymentSuccess ? (
                <div className="flex items-center gap-2 text-small text-green-600 py-4">
                  <CheckCircle2 size={16} />
                  <span>Payment method saved successfully.</span>
                </div>
              ) : (
                <Elements
                  stripe={stripePromise}
                  options={{
                    clientSecret: setupClientSecret,
                    appearance: {
                      theme: 'stripe',
                      variables: {
                        colorPrimary: '#820263',
                        fontFamily: 'Nunito Sans, sans-serif',
                        borderRadius: '10px',
                      },
                    },
                  }}
                >
                  <PaymentMethodForm onSuccess={() => setPaymentSuccess(true)} />
                </Elements>
              )}
            </>
          ) : !stripeConfigured ? (
            <div className="py-6 text-center">
              <CreditCard size={32} className="mx-auto text-stone mb-3" />
              <p className="text-small text-slate">
                Payment method management will be available once Stripe is configured.
              </p>
            </div>
          ) : (
            <div className="py-6 text-center">
              <Loader2 size={24} className="mx-auto animate-spin text-plum mb-3" />
              <p className="text-small text-slate">Loading payment form…</p>
            </div>
          )}
        </section>
      )}

      {/* ── Section: Invoices ────────────────────────────────────────── */}
      {!loadingBilling && hasSubscription && (
        <section className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm mb-4">
          <div className="flex items-center gap-2 mb-4">
            <FileText size={18} className="text-plum" />
            <h2 className="font-display text-h3 text-midnight">Invoices</h2>
          </div>

          {invoicesLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={20} className="animate-spin text-plum" />
            </div>
          )}

          {!invoicesLoading && invoices.length === 0 && (
            <div className="py-8 text-center">
              <FileText size={32} className="mx-auto text-stone mb-3" />
              <p className="text-small text-slate">No invoices yet.</p>
            </div>
          )}

          {!invoicesLoading && invoices.length > 0 && (
            <ul className="divide-y divide-stone">
              {invoices.map((inv) => (
                <li key={inv.id} className="py-3 first:pt-0 last:pb-0">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-small font-medium text-midnight">
                          {inv.number ?? inv.id.slice(0, 16)}
                        </p>
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium uppercase tracking-wide ${invoiceStatusColor(inv.status)}`}
                        >
                          {inv.status ?? 'unknown'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-[13px] text-slate">
                          {formatDate(inv.created)}
                        </p>
                        <span className="text-[13px] text-slate">·</span>
                        <p className="text-[13px] font-medium text-midnight">
                          {formatCurrency(inv.amountDue, inv.currency)}
                        </p>
                      </div>
                    </div>
                    {inv.hostedInvoiceUrl && (
                      <a
                        href={inv.hostedInvoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 w-10 h-10 flex items-center justify-center rounded-lg
                                   hover:bg-mist active:bg-stone transition-colors"
                      >
                        <ExternalLink size={16} className="text-plum" />
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
